import { computed } from 'vue';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';
import type { OrderFeatureKey } from '@cutr/constants/order-features';
import {
  useOrderFeatureMutations,
  useOrderFeaturesQuery,
  useTogglingFeatures,
} from '@/queries/orderFeatures';
import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';

// Hosts the features query and the toggle observer once. One response covers
// every feature key, and there is a consumer per key.
export const useOrderFeaturesStore = defineStore('orderFeatures', () => {
  const { currentOrder } = storeToRefs(useOrderStore());
  const { isLoggedIn } = storeToRefs(useActiveUserStore());
  const orderId = computed(() => currentOrder.value.id);
  const { features, query } = useOrderFeaturesQuery(orderId, {
    enabled: isLoggedIn,
  });
  const togglingFeatures = useTogglingFeatures();
  const mutations = useOrderFeatureMutations();

  const isLoading = computed(() => query.isLoading.value);

  function getFeature(featureKey: OrderFeatureKey) {
    return features.value[featureKey];
  }

  function isToggling(featureKey: OrderFeatureKey) {
    return togglingFeatures.value.some(
      (variables) =>
        variables?.featureKey === featureKey &&
        variables.orderId === currentOrder.value.id
    );
  }

  return {
    features,
    isLoading,
    getFeature,
    isToggling,
    ...mutations,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useOrderFeaturesStore, import.meta.hot)
  );
}
